import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from './UserContext';
import { useClients } from '../contexts/DataContext';
import { usePendientesDecorados } from '../hooks/usePendientes';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import { usePendienteContexto } from '../contexts/PendienteContexto';
import PendienteDialog from './PendienteDialog';
import PendienteNotas from './PendienteNotas';
import { logBitacora } from '../utils/bitacora';
import {
  PENDIENTE_TIPOS,
  PENDIENTE_TIPO_LABELS,
  TARGET_TIPOS,
  ORG_LABEL,
  buildPendiente,
  buildResolucion,
  esDeLaOrg,
  resolverTarget,
  ordenarPorUrgencia,
  estaAsignado,
  normalizarAsignados,
  ACTIVIDAD_LABELS,
  isAbierto,
} from '../utils/pendientes';
import {
  ClipboardDocumentListIcon, XMarkIcon, CheckCircleIcon, PlusIcon, ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';

const STORAGE_KEY = 'pendientes:burbuja';

const URGENCIA_DOT = {
  vencido: 'bg-red-500',
  urgente: 'bg-amber-500',
  proximo: 'bg-cian-digital',
  normal: 'bg-zinc-300',
  sinFecha: 'bg-zinc-200',
};

// "creado hace 2 d" or "act. hace 3 h": the last time anything happened to
// the row (a note, a reassignment, an edit), falling back to creation.
const haceTexto = (ms, nowMs = Date.now()) => {
  const diff = Math.max(0, nowMs - Number(ms || 0));
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} d`;
  const m = Math.floor(d / 30);
  return `hace ${m} mes${m === 1 ? '' : 'es'}`;
};
const actividadTexto = (row) => {
  const creado = Number(row.createdAt || 0);
  const actualizado = Number(row.updatedAt || 0);
  if (actualizado > creado + 60000) return `act. ${haceTexto(actualizado)}`;
  return `creado ${haceTexto(creado)}`;
};
const fechaLarga = (ms) => (ms ? new Date(Number(ms)).toLocaleString('es-MX', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '');

const urgenciaTexto = (row) => {
  if (row.urgencia === 'vencido') return `${Math.abs(row.diasRestantes)} d de retraso`;
  if (row.urgencia === 'sinFecha') return 'sin fecha';
  if (row.diasRestantes === 0) return 'hoy';
  return `en ${row.diasRestantes} d`;
};

// Tipos the bolita offers, by where the user is standing. Cliente context is
// where compromisos get registered day to day — Fran: "que los compromisos
// solo salgan a nivel fianza es muy poco, hay que escarbar mucho".
const TIPOS_CLIENTE = [
  PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA,
  PENDIENTE_TIPOS.DOCUMENTO,
  PENDIENTE_TIPOS.ACTA_ENTREGA,
  PENDIENTE_TIPOS.OTRO,
];
const TIPOS_ORG = [PENDIENTE_TIPOS.OTRO];
// Inside a contrato or a fianza the tipos are the ones that live there (the
// tipo dictates the level), plus the free-form one.
const TIPOS_CONTRATO = [
  PENDIENTE_TIPOS.ACTA_ENTREGA,
  PENDIENTE_TIPOS.CONVENIO_MODIFICATORIO,
  PENDIENTE_TIPOS.OTRO,
];
const TIPOS_FIANZA = [PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA, PENDIENTE_TIPOS.OTRO];

/**
 * La bolita: the always-there pendientes surface, bottom-right, for brokers.
 *
 * Sticky-note mentality, not a section: one click opens it, a title and Enter
 * files a pendiente where you are standing (the cliente whose page is open, or
 * the org when nowhere in particular), and the list shows what is yours and
 * what belongs to the thing on screen. The full list with rollups stays on
 * /app/pendientes; this replaced the "Pendientes" tab in ClienteDetails.
 *
 * Reads the single org-wide listener through usePendientesDecorados — no
 * subscription of its own.
 */
export function PendientesBurbuja() {
  const { user, userData, isClient, isOwner } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { clientsById } = useClients();
  const { rows: todas, abiertos, loading } = usePendientesDecorados();
  const { miembros, nombreDe } = useOrgMiembros();

  const [abierta, setAbierta] = useState(() => {
    try { return window.localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });
  const [vista, setVista] = useState(null); // 'aqui' | 'mios' | 'compromisos' | 'equipo'
  // Equipo view: whose list an owner is looking at ('' = everyone else's).
  const [miembroVisto, setMiembroVisto] = useState('');
  const [titulo, setTitulo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notasAbiertas, setNotasAbiertas] = useState(null);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, abierta ? '1' : '0'); } catch { /* private mode */ }
  }, [abierta]);

  // Context = the narrowest thing on screen. ContratoView / FianzaView are
  // modals, not routes, so they register themselves (PendienteContexto);
  // otherwise the cliente page's route is the context.
  const modal = usePendienteContexto();
  const clienteRuta = useMemo(() => {
    const m = location.pathname.match(/^\/app\/clientes\/([^/]+)$/);
    return m && m[1] !== 'archivados' ? m[1] : null;
  }, [location.pathname]);
  const contexto = useMemo(
    () => modal || (clienteRuta ? { clienteId: clienteRuta } : null),
    [modal, clienteRuta]
  );
  const clienteId = contexto?.clienteId || null;
  const cliente = clienteId ? clientsById[clienteId] : null;
  const contextoLabel = contexto?.fianzaId
    ? `Fianza #${contexto.numeroFianza || ''}`.trim()
    : contexto?.contratoId
      // The number identifies a contrato; the título only when there is none.
      ? (contexto.numeroContrato ? `Contrato ${contexto.numeroContrato}` : (contexto.contratoTitulo || 'Este contrato'))
      : (cliente?.nombreEmpresa || 'Este fiado');
  const tiposContexto = contexto?.fianzaId ? TIPOS_FIANZA
    : contexto?.contratoId ? TIPOS_CONTRATO
      : clienteId ? TIPOS_CLIENTE
        : TIPOS_ORG;

  const enPortal = location.pathname.startsWith('/app/portal');

  const mios = useMemo(
    () => ordenarPorUrgencia(abiertos.filter((p) => estaAsignado(p, user?.uid))),
    [abiertos, user?.uid]
  );
  const aqui = useMemo(() => {
    if (!contexto) return [];
    const match = contexto.fianzaId ? (p) => p.fianzaId === contexto.fianzaId
      : contexto.contratoId ? (p) => p.contratoId === contexto.contratoId
        : contexto.convenioId ? (p) => p.convenioId === contexto.convenioId
          : (p) => p.clienteId === contexto.clienteId;
    return ordenarPorUrgencia(abiertos.filter(match));
  }, [abiertos, contexto]);
  const compromisos = useMemo(
    () => ordenarPorUrgencia(
      abiertos.filter((p) => p.tipo === PENDIENTE_TIPOS.COMPROMISO_AFIANZADORA)
    ),
    [abiertos]
  );
  const atencion = mios.filter((p) => p.urgencia === 'vencido' || p.urgencia === 'urgente').length;

  // What the rest of the team carries. Owner-only: an operator's bolita is
  // their own list, the supervision view is the owner's. With Órdenes de
  // Trabajo this becomes the supervisor's inbox.
  const equipo = useMemo(() => {
    if (!isOwner) return [];
    const ajenos = abiertos.filter((p) => normalizarAsignados(p).some((u) => u !== user?.uid));
    return ordenarPorUrgencia(
      miembroVisto ? ajenos.filter((p) => estaAsignado(p, miembroVisto)) : ajenos
    );
  }, [abiertos, isOwner, user?.uid, miembroVisto]);

  // " · Ana, Luis" — the assignees that are not me, for rows in shared views.
  const otrosAsignados = (row) => {
    const otros = normalizarAsignados(row).filter((u) => u !== user?.uid).map(nombreDe).filter(Boolean);
    return otros.length ? ` · ${otros.join(', ')}` : '';
  };

  // Live feed for the owner: whatever moved last, across the org, from the
  // ultimaActividad stamp every write carries. Includes closed rows so
  // "Ana resolvió X" shows up, capped to the last 7 days.
  const actividad = useMemo(() => {
    if (!isOwner) return [];
    const desde = Date.now() - 7 * 86400000;
    return (todas || [])
      .filter((p) => p.ultimaActividad && Number(p.ultimaActividad.at || 0) >= desde)
      .sort((a, b) => Number(b.ultimaActividad.at) - Number(a.ultimaActividad.at))
      .slice(0, 40);
  }, [todas, isOwner]);

  const vistaEfectiva = vista || (contexto ? 'aqui' : 'mios');
  const filas = vistaEfectiva === 'aqui' ? aqui
    : vistaEfectiva === 'compromisos' ? compromisos
      : vistaEfectiva === 'equipo' ? equipo
        : vistaEfectiva === 'actividad' ? actividad
          : mios;

  if (isClient || enPortal || !userData?.org) return null;

  const registrarRapido = async () => {
    const limpio = titulo.trim();
    if (!limpio || guardando) return;
    setGuardando(true);
    try {
      const resolved = resolverTarget(PENDIENTE_TIPOS.OTRO, contexto || {});
      await addDoc(collection(db, 'pendientes'), buildPendiente({
        org: userData.org,
        ...resolved,
        clienteId: clienteId || null,
        convenioId: contexto?.convenioId || null,
        contratoId: contexto?.contratoId || null,
        fianzaId: contexto?.fianzaId || null,
        afianzadoraRaw: contexto?.afianzadoraRaw || null,
        tipo: PENDIENTE_TIPOS.OTRO,
        titulo: limpio,
        asignados: user?.uid ? [user.uid] : [],
        createdBy: { uid: user?.uid || null, nombre: userData?.nombre || userData?.correo || null },
      }));
      try {
        await logBitacora({
          action: 'CREATE',
          collection: contexto?.convenioId ? 'convenios' : clienteId ? 'clientes' : 'orgs',
          documentId: contexto?.convenioId || clienteId || userData.org,
          user,
          userData,
          metadata: {
            description: `Registró pendiente de ${contexto ? contextoLabel : 'la organización'}: ${limpio}`,
            clientId: clienteId || null,
            contratoId: contexto?.contratoId || null,
            pendienteTipo: PENDIENTE_TIPOS.OTRO,
          },
        });
      } catch (e) { console.error('bitacora burbuja failed', e); }
      setTitulo('');
      if (resolved.targetTipo === TARGET_TIPOS.ORG) setVista('mios');
    } catch (e) {
      console.error('registrar rápido failed', e);
    } finally {
      setGuardando(false);
    }
  };

  const resolver = async (row) => {
    setBusyId(row.id);
    try {
      await updateDoc(doc(db, 'pendientes', row.id),
        buildResolucion({ resueltoCon: { tipo: 'manual' }, user, nombre: userData?.nombre || userData?.correo || null }));
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: row.convenioId ? 'convenios' : row.clienteId ? 'clientes' : 'orgs',
          documentId: row.convenioId || row.clienteId || userData.org,
          user,
          userData,
          metadata: { description: `Resolvió pendiente: ${row.titulo}`, clientId: row.clienteId || null },
        });
      } catch (e) { console.error('bitacora burbuja failed', e); }
    } catch (e) {
      console.error('resolver pendiente failed', e);
    } finally {
      setBusyId(null);
    }
  };

  const abrir = (row) => {
    if (row.clienteId) return navigate(`/app/clientes/${row.clienteId}`);
    if (row.convenioId) return navigate(`/app/convenios/${row.convenioId}`);
    return navigate('/app/pendientes');
  };

  const tabs = [
    ...(contexto ? [{ key: 'aqui', label: contextoLabel, n: aqui.length }] : []),
    { key: 'mios', label: 'Míos', n: mios.length },
    ...(isOwner ? [{ key: 'equipo', label: 'Equipo', n: equipo.length }] : []),
    ...(isOwner ? [{ key: 'actividad', label: 'Actividad', n: 0 }] : []),
    { key: 'compromisos', label: 'Compromisos', n: compromisos.length },
  ];

  return (
    <>
      {/* z-[60]: above the ContratoView/FianzaView overlays (z-50), so the
          bolita stays reachable inside them, but below the Catalyst dialogs
          (z-[70]) so the PendienteDialog it opens lands on top of it. */}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col items-end gap-2 print:hidden">
        {abierta && (
          <div className="w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl border border-zinc-200 bg-white shadow-xl flex flex-col max-h-[70vh]">
            <div className="flex items-start justify-between gap-2 px-3 pt-3 pb-2 border-b border-zinc-100">
              {/* Wrap, never scroll: four tabs inside a contrato overflowed
                  the 22rem panel and grew a horizontal scrollbar. */}
              <div className="flex flex-wrap items-center gap-1 min-w-0">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setVista(t.key)}
                    className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
                      vistaEfectiva === t.key
                        ? 'bg-azul-surelia text-white'
                        : 'text-zinc-600 hover:bg-zinc-100'
                    }`}
                  >
                    <span className="max-w-[10rem] truncate inline-block align-bottom">{t.label}</span>
                    {t.n > 0 && <span className="ml-1 opacity-80">{t.n}</span>}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setAbierta(false)}
                title="Cerrar"
                className="p-1 rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 flex-shrink-0"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>

            {vistaEfectiva === 'equipo' && (
              <div className="px-3 py-2 border-b border-zinc-100">
                <select
                  value={miembroVisto}
                  onChange={(e) => setMiembroVisto(e.target.value)}
                  className="w-full rounded-lg border border-zinc-200 px-2.5 py-1.5 text-sm outline-none focus:border-azul-surelia"
                >
                  <option value="">Todo el equipo</option>
                  {miembros.filter((m) => m.id !== user?.uid).map((m) => (
                    <option key={m.id} value={m.id}>{m.nombre || m.correo}</option>
                  ))}
                </select>
              </div>
            )}

            {vistaEfectiva !== 'compromisos' && vistaEfectiva !== 'equipo' && vistaEfectiva !== 'actividad' && (
              <div className="px-3 py-2 border-b border-zinc-100">
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    value={titulo}
                    maxLength={140}
                    disabled={guardando}
                    onChange={(e) => setTitulo(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') registrarRapido(); }}
                    placeholder={contexto ? `Pendiente de ${contextoLabel}` : `Pendiente de ${ORG_LABEL.toLowerCase()}`}
                    className="flex-1 min-w-0 rounded-lg border border-zinc-200 px-2.5 py-1.5 text-sm outline-none focus:border-azul-surelia focus:ring-1 focus:ring-azul-surelia"
                  />
                  <button
                    onClick={() => setDialogOpen(true)}
                    title="Con tipo, fecha y detalle"
                    className="p-1.5 rounded-lg border border-zinc-200 text-azul-surelia hover:bg-azul-surelia/5"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-y-auto divide-y divide-zinc-100">
              {loading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-azul-surelia"></div>
                </div>
              ) : filas.length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-zinc-400">Nada abierto.</div>
              ) : filas.map((row) => (
                <div key={row.id} className="px-3 py-2">
                <div className="flex items-start gap-2">
                  <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${URGENCIA_DOT[row.urgencia] || URGENCIA_DOT.normal}`} />
                  <div className="min-w-0 flex-1 text-left">
                    <div className="flex items-baseline justify-between gap-2">
                      <button
                        onClick={() => abrir(row)}
                        title={row.clienteId ? 'Ir al fiado' : row.convenioId ? 'Ir al convenio' : 'Ver en Pendientes'}
                        className="text-sm text-zinc-800 truncate text-left hover:text-azul-surelia"
                      >
                        {row.titulo}
                      </button>
                      <span
                        className="text-[10px] text-zinc-400 whitespace-nowrap flex-shrink-0"
                        title={`Creado ${fechaLarga(row.createdAt)}${
                          Number(row.updatedAt || 0) > Number(row.createdAt || 0) + 60000
                            ? ` · actualizado ${fechaLarga(row.updatedAt)}` : ''}`}
                      >
                        {actividadTexto(row)}
                      </span>
                    </div>
                    {vistaEfectiva === 'actividad' && row.ultimaActividad && (
                      <div className="text-xs text-azul-surelia truncate">
                        {row.ultimaActividad.por === user?.uid ? 'Tú' : (row.ultimaActividad.nombre || nombreDe(row.ultimaActividad.por) || 'Alguien')}
                        {' '}{ACTIVIDAD_LABELS[row.ultimaActividad.tipo] || row.ultimaActividad.tipo}
                        {row.ultimaActividad.texto ? `: ${row.ultimaActividad.texto}` : ''}
                        {' · '}{haceTexto(row.ultimaActividad.at)}
                      </div>
                    )}
                    <div className="text-xs text-zinc-500 truncate">
                      {vistaEfectiva === 'compromisos' && `${row.afianzadora || 'Sin afianzadora'} · `}
                      {esDeLaOrg(row) ? ORG_LABEL : (
                        <button
                          onClick={() => abrir(row)}
                          title={row.convenioId ? 'Ir al convenio' : 'Ir al fiado'}
                          className="text-azul-surelia hover:underline"
                        >
                          {row.clientName}{row.convenioId ? ' (convenio)' : ''}
                        </button>
                      )}
                      {' · '}{urgenciaTexto(row)}
                      {vistaEfectiva !== 'compromisos' && row.tipo !== PENDIENTE_TIPOS.OTRO
                        ? ` · ${PENDIENTE_TIPO_LABELS[row.tipo] || row.tipo}` : ''}
                      {vistaEfectiva !== 'mios' && !(vistaEfectiva === 'equipo' && miembroVisto)
                        ? otrosAsignados(row) : ''}
                    </div>
                  </div>
                  <button
                    title="Actualizaciones"
                    onClick={() => setNotasAbiertas((v) => (v === row.id ? null : row.id))}
                    className={`p-1 rounded hover:bg-zinc-100 flex-shrink-0 inline-flex items-center gap-0.5 text-[10px] ${
                      notasAbiertas === row.id ? 'text-azul-surelia' : 'text-zinc-400'
                    }`}
                  >
                    <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />
                    {(row.notasInternas || []).length > 0 ? row.notasInternas.length : ''}
                  </button>
                  {isAbierto(row) && (
                  <button
                    title="Marcar resuelto"
                    disabled={busyId === row.id}
                    onClick={() => resolver(row)}
                    className="p-1 rounded text-green-600 hover:bg-green-50 flex-shrink-0"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                  </button>
                  )}
                </div>
                {notasAbiertas === row.id && (
                  <div className="mt-2 pl-4">
                    <PendienteNotas pendiente={row} compacto />
                  </div>
                )}
                </div>
              ))}
            </div>

            <div className="px-3 py-2 border-t border-zinc-100 flex justify-end">
              <button
                onClick={() => navigate('/app/pendientes')}
                className="text-xs text-azul-surelia hover:underline"
              >
                Ver todos
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => setAbierta((v) => !v)}
          title={abierta ? 'Cerrar pendientes' : 'Pendientes'}
          className="relative h-12 w-12 rounded-full bg-azul-surelia text-white shadow-lg hover:bg-azul-surelia/90 flex items-center justify-center"
        >
          <ClipboardDocumentListIcon className="w-6 h-6" />
          {mios.length > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[1.25rem] h-5 rounded-full px-1 text-[11px] font-bold flex items-center justify-center ${
              atencion > 0 ? 'bg-red-500 text-white' : 'bg-white text-azul-surelia border border-azul-surelia/30'
            }`}>
              {mios.length}
            </span>
          )}
        </button>
      </div>

      <PendienteDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        preset={contexto ? {
          clienteId: contexto.clienteId || null,
          convenioId: contexto.convenioId || null,
          contratoId: contexto.contratoId || null,
          fianzaId: contexto.fianzaId || null,
          contratoTitulo: contexto.contratoTitulo || '',
          numeroFianza: contexto.numeroFianza || '',
          afianzadoraRaw: contexto.afianzadoraRaw || null,
        } : null}
        tiposPermitidos={tiposContexto}
      />
    </>
  );
}

export default PendientesBurbuja;
